/**
 * Admin for owners and managers (and FMS staff on a customer's behalf): people and their
 * limits, what each role can do, sites and gateways, farm settings, activity log.
 */
import { useMemo, useState } from "react";
import { api, ApiError, type AccountSettings, type Crop, type Device, type Plan, type PlanInfo, type PublicUser, type Role, type Site } from "../api.ts";
import { can } from "../control.ts";
import { LockCard, useCan } from "../plan.tsx";
import { ago, clock, deviceStatus, formatEui, STATUS_TONE } from "../format.ts";
import { assignableRoles, type Permission } from "../../../../packages/core/src/permissions.ts";
import { hoursText } from "../../../../packages/core/src/access.ts";
import { Problem, Section, Spinner, useLoad, useNow } from "../ui.tsx";
import { AUDIT_NAMES } from "../audit-names.ts";
import { emailProblem } from "../../../../packages/core/src/checklist.ts";
import { OwnerPayment } from "./paypal.tsx";

type Tab = "people" | "roles" | "sites" | "crops" | "settings" | "plan" | "log";
const ROLE_NAMES: Record<string, string> = { owner: "Owner", manager: "Manager", operator: "Operator", viewer: "Viewer", installer: "Installer", "fms-admin": "FMS staff" };
const ROLE_TEXT: Record<string, string> = {
  owner: "Everything, including billing. One per account.",
  manager: "Runs the farm day to day: switching, rules, set-up and people.",
  operator: "Sees everything and switches equipment.",
  viewer: "Sees readings and alerts. Can't change anything.",
  installer: "Sets up devices and ports. Give them an end date.",
};
const PERMS: Array<[Permission, string]> = [
  ["view", "See readings, alerts and reports"],
  ["control", "Switch pumps and valves"],
  ["edit-rules", "Change safety rules, timers and programs"],
  ["override-safety-rules", "Turn a board's safety rule off for a while, back on, or reset it"],
  ["setup-devices", "Set up devices and ports"],
  ["manage-users", "Invite people and change their access"],
  ["billing", "Plan and billing"],
];
const message = (e: unknown) => (e instanceof ApiError || e instanceof Error ? e.message : "Something went wrong.");

export function Admin({ accountId, staff, role, myId, sites, tab: start, onSitesChanged }: { accountId: string | undefined; staff: boolean; role: string; myId: string; sites: Site[]; tab?: string; onSitesChanged?: () => void }) {
  const [tab, setTab] = useState<Tab>(start === "plan" ? "plan" : "people");
  const id = staff ? accountId : undefined;
  if (!can(role, "manage-users")) return <div className="card empty">Only the owner and managers can see Admin.</div>;
  const tabs: Array<[Tab, string]> = [
    ["people", "People"],
    ["roles", "Roles"],
    ["sites", "Sites and gateways"],
    ["crops", "Crops"],
    ["settings", "Farm settings"],
    ["plan", "Plan"],
    ["log", "Activity log"],
  ];
  return (
    <>
      <div className="chips" role="tablist">
        {tabs.map(([t, l]) => (
          <button key={t} role="tab" className="chip" aria-pressed={tab === t} aria-selected={tab === t} onClick={() => setTab(t)}>
            {l}
          </button>
        ))}
      </div>
      {tab === "people" && <People accountId={id} myRole={role} myId={myId} />}
      {tab === "roles" && <Roles />}
      {tab === "sites" && <SitesAndGateways accountId={id} sites={sites} canEdit={can(role, "setup-devices")} onSitesChanged={onSitesChanged} />}
      {tab === "crops" && <Crops accountId={id} canEdit={can(role, "edit-rules")} />}
      {tab === "settings" && <Settings accountId={id} />}
      {tab === "plan" && <PlanTab accountId={id} staff={staff} role={role} />}
      {tab === "log" && <Log accountId={id} />}
    </>
  );
}

// ------------------------------------------------------------------ people

function People({ accountId, myRole, myId }: { accountId: string | undefined; myRole: string; myId: string }) {
  const users = useLoad(() => api.accountUsers(accountId), [accountId]);
  const devices = useLoad(() => api.devices(accountId), [accountId]);
  const sites = useLoad(() => api.sites(accountId), [accountId]);
  const [editing, setEditing] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);
  const now = useNow();
  if (users.error && !users.data) return <Problem error={users.error} onRetry={users.reload} />;
  if (!users.data || !devices.data || !sites.data) return <Spinner />;
  const roles = assignableRoles({ role: myRole as Role });
  const allDevices = devices.data.devices;
  const allSites = sites.data.sites;
  const list = [...users.data.users].sort((a, b) => Number(a.status === "disabled") - Number(b.status === "disabled") || a.name.localeCompare(b.name));
  return (
    <Section
      title={`People · ${users.data.users.filter((u) => u.status !== "disabled").length}`}
      right={
        !inviting && (
          <button className="btn primary" onClick={() => setInviting(true)}>
            + Invite someone
          </button>
        )
      }
    >
      {inviting && (
        <InviteForm
          accountId={accountId}
          roles={roles}
          onDone={() => {
            setInviting(false);
            users.reload();
          }}
          onCancel={() => setInviting(false)}
        />
      )}
      <div className="card list">
        {list.map((u) => (
          <div key={u.id}>
            <div className="person">
              <span className={`dot ${u.status === "active" ? "good" : u.status === "invited" ? "warn" : u.status === "disabled" ? "crit" : ""}`} />
              <div style={{ minWidth: 0 }}>
                <div className="t">
                  {u.name} <span className="muted small">· {ROLE_NAMES[u.role] ?? u.role}</span>
                  {u.id === myId && <span className="muted small"> (you)</span>}
                </div>
                <div className="d">
                  {u.email}
                  {u.phone ? ` · ${u.phone}` : ""}
                </div>
                <div className="d">{limitsText(u, allDevices.length, allSites)}</div>
                <div className="d">
                  {u.status === "active"
                    ? u.activatedAt
                      ? `Joined ${clock(u.activatedAt, now)}`
                      : "Active"
                    : u.status === "invited"
                      ? `Invited ${u.invitedAt ? ago(u.invitedAt, now) : ""}, hasn't signed in yet`
                      : u.status === "draft"
                        ? "Draft: invited when the account goes live"
                        : "Access removed"}
                </div>
              </div>
              {u.id !== myId && (u.role !== "owner" || myRole === "fms-admin") && (
                <button className="btn" onClick={() => setEditing(editing === u.id ? null : u.id)}>
                  {editing === u.id ? "Close" : "Change"}
                </button>
              )}
            </div>
            {editing === u.id && (
              <EditUser
                u={u}
                roles={roles}
                devices={allDevices}
                sites={allSites}
                onDone={() => {
                  users.reload();
                }}
              />
            )}
          </div>
        ))}
      </div>
    </Section>
  );
}

function limitsText(u: PublicUser, total: number, sites: Site[]): string {
  const farms = u.sites?.map((id) => sites.find((s) => s.id === id)?.name ?? "?");
  const parts = [
    farms?.length
      ? `Farm${farms.length === 1 ? "" : "s"} ${farms.join(", ")}${u.devices?.length ? ` + ${u.devices.length} device${u.devices.length === 1 ? "" : "s"}` : ""}`
      : u.devices
        ? `${u.devices.length} of ${total} devices`
        : "All devices",
  ];
  if (u.controlHours && can(u.role, "control")) parts.push(`switches ${hoursText(u.controlHours)}`);
  if (u.accessExpiresAt) parts.push(`access ends ${new Date(u.accessExpiresAt).toLocaleDateString("en-AU")}`);
  return parts.join(" · ");
}

function InviteForm({ accountId, roles, onDone, onCancel }: { accountId: string | undefined; roles: Role[]; onDone: () => void; onCancel: () => void }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState<Role>(roles.includes("operator") ? "operator" : roles[0]!);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  return (
    <form
      className="card pad"
      style={{ display: "flex", flexDirection: "column", gap: 10 }}
      onSubmit={async (e) => {
        e.preventDefault();
        const bad = emailProblem(email);
        if (bad) return setError(bad);
        setBusy(true);
        setError("");
        try {
          await api.inviteUser({ name: name.trim(), email: email.trim(), role, ...(phone.trim() ? { phone: phone.trim() } : {}) }, accountId);
          onDone();
        } catch (err) {
          setError(message(err));
        } finally {
          setBusy(false);
        }
      }}
    >
      <h3 style={{ fontSize: 17 }}>Invite someone</h3>
      <div className="frow">
        <div className="field">
          <label htmlFor="iName">Name</label>
          <input id="iName" value={name} onChange={(e) => setName(e.target.value)} required autoComplete="off" />
        </div>
        <div className="field">
          <label htmlFor="iEmail">Email</label>
          <input id="iEmail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="off" />
        </div>
        <div className="field">
          <label htmlFor="iPhone">Mobile (for SMS alerts)</label>
          <input id="iPhone" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="off" />
        </div>
        <div className="field">
          <label htmlFor="iRole">Role</label>
          <select id="iRole" value={role} onChange={(e) => setRole(e.target.value as Role)}>
            {roles.map((r) => (
              <option key={r} value={r}>
                {ROLE_NAMES[r]}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="muted small">
        {ROLE_NAMES[role]}: {ROLE_TEXT[role]} They'll get an email to choose a password. You can limit their devices and hours after.
      </div>
      {error && <div className="error">{error}</div>}
      <div className="toolbar">
        <button className="btn primary" disabled={busy}>
          {busy ? "Sending…" : "Send invite"}
        </button>
        <button type="button" className="btn" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function EditUser({ u, roles, devices, sites, onDone }: { u: PublicUser; roles: Role[]; devices: Device[]; sites: Site[]; onDone: () => void }) {
  const [role, setRole] = useState<Role>(u.role);
  // all = every device; farms = whole sites (new devices there included); some = named devices
  const [scope, setScope] = useState<"all" | "farms" | "some">(u.sites?.length ? "farms" : u.devices ? "some" : "all");
  const some = scope === "some";
  const [picked, setPicked] = useState<Set<string>>(new Set(u.devices ?? []));
  const [pickedSites, setPickedSites] = useState<Set<string>>(new Set(u.sites ?? []));
  const [limitHours, setLimitHours] = useState(!!u.controlHours);
  const [from, setFrom] = useState(u.controlHours?.from ?? "05:00");
  const [to, setTo] = useState(u.controlHours?.to ?? "21:00");
  const [ends, setEnds] = useState(u.accessExpiresAt ? new Date(u.accessExpiresAt).toISOString().slice(0, 10) : "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError("");
    setSaved(false);
    try {
      await fn();
      setSaved(true);
      onDone();
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  };
  const save = () =>
    run(() =>
      api.changeUser(u.id, {
        ...(role !== u.role ? { role } : {}),
        devices: scope === "some" ? [...picked] : null,
        sites: scope === "farms" ? [...pickedSites] : null,
        controlHours: limitHours && can(role, "control") ? { from, to } : null,
        accessExpiresAt: ends ? new Date(`${ends}T23:59:59`).getTime() : null,
      }),
    );
  const options = roles.includes(u.role) ? roles : [u.role, ...roles];
  return (
    <div className="port-body" style={{ borderTop: "1px solid var(--line)" }}>
      <div className="frow">
        <div className="field">
          <label htmlFor={`r${u.id}`}>Role</label>
          <select id={`r${u.id}`} value={role} onChange={(e) => setRole(e.target.value as Role)}>
            {options.map((r) => (
              <option key={r} value={r} disabled={!roles.includes(r)}>
                {ROLE_NAMES[r]}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor={`e${u.id}`}>Access ends (optional)</label>
          <input id={`e${u.id}`} type="date" value={ends} onChange={(e) => setEnds(e.target.value)} />
        </div>
      </div>
      <div className="muted small">{ROLE_TEXT[role]}</div>
      <div>
        <div className="sub-h">Which devices</div>
        <div className="checks">
          <label className="checkline">
            <input type="radio" name={`d${u.id}`} checked={scope === "all"} onChange={() => setScope("all")} /> All devices, including new ones
          </label>
          {sites.length > 1 && (
            <label className="checkline">
              <input type="radio" name={`d${u.id}`} checked={scope === "farms"} onChange={() => setScope("farms")} /> Only these farms (every device there, including new ones)
            </label>
          )}
          <label className="checkline">
            <input type="radio" name={`d${u.id}`} checked={scope === "some"} onChange={() => setScope("some")} /> Only these devices
          </label>
        </div>
        {scope === "farms" && (
          <div className="chips" style={{ marginTop: 8 }}>
            {sites.map((s) => (
              <button
                key={s.id}
                className="chip"
                aria-pressed={pickedSites.has(s.id)}
                onClick={() => {
                  const next = new Set(pickedSites);
                  if (next.has(s.id)) next.delete(s.id);
                  else next.add(s.id);
                  setPickedSites(next);
                }}
              >
                {s.name}
              </button>
            ))}
          </div>
        )}
        {some && (
          <div className="chips" style={{ marginTop: 8 }}>
            {devices.map((d) => (
              <button
                key={d.devEui}
                className="chip"
                aria-pressed={picked.has(d.devEui)}
                onClick={() => {
                  const next = new Set(picked);
                  if (next.has(d.devEui)) next.delete(d.devEui);
                  else next.add(d.devEui);
                  setPicked(next);
                }}
              >
                {d.name}
              </button>
            ))}
          </div>
        )}
      </div>
      {can(role, "control") && (
        <div>
          <div className="sub-h">When they can switch equipment</div>
          <div className="checks">
            <label className="checkline">
              <input type="radio" name={`h${u.id}`} checked={!limitHours} onChange={() => setLimitHours(false)} /> Any time
            </label>
            <label className="checkline">
              <input type="radio" name={`h${u.id}`} checked={limitHours} onChange={() => setLimitHours(true)} /> Only between
            </label>
            {limitHours && (
              <span className="toolbar">
                <input type="time" className="num-in" value={from} onChange={(e) => setFrom(e.target.value)} aria-label="From" />
                and
                <input type="time" className="num-in" value={to} onChange={(e) => setTo(e.target.value)} aria-label="To" />
              </span>
            )}
          </div>
          <div className="muted small">Times are the farm's local time. Safety rules and timers on the boards keep working regardless.</div>
        </div>
      )}
      {some && picked.size === 0 && <div className="banner warn small">With no devices ticked, {u.name} won't see any devices.</div>}
      {error && <div className="error">{error}</div>}
      <div className="toolbar">
        <button className="btn primary" disabled={busy} onClick={() => void save()}>
          Save
        </button>
        {saved && <span className="muted small">Saved</span>}
        <span style={{ flex: 1 }} />
        {u.status === "invited" && (
          <button className="btn" disabled={busy} onClick={() => void run(() => api.resendUserInvite(u.id))}>
            Resend invite
          </button>
        )}
        {u.status === "draft" ? (
          <button className="btn danger" disabled={busy} onClick={() => confirm(`Remove ${u.name}? They were never invited, so nothing else changes.`) && void run(() => api.removeDraftUser(u.id))}>
            Remove user
          </button>
        ) : u.status === "disabled" ? (
          <button className="btn" disabled={busy} onClick={() => void run(() => api.restoreAccess(u.id))}>
            Give access back
          </button>
        ) : (
          <button className="btn danger" disabled={busy} onClick={() => confirm(`Remove ${u.name}'s access? They won't be able to sign in. Their history stays.`) && void run(() => api.removeAccess(u.id))}>
            Remove access
          </button>
        )}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ roles

function Roles() {
  const roles: Role[] = ["owner", "manager", "operator", "viewer", "installer"];
  return (
    <Section title="What each role can do">
      <div className="card tbl-wrap">
        <table className="tbl">
          <thead>
            <tr>
              <th>Permission</th>
              {roles.map((r) => (
                <th key={r} style={{ textAlign: "center" }}>
                  {ROLE_NAMES[r]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PERMS.map(([p, label]) => (
              <tr key={p}>
                <td>{label}</td>
                {roles.map((r) => (
                  <td key={r} style={{ textAlign: "center" }}>
                    {can(r, p) ? <span className="yes" aria-label="Yes">✓</span> : <span className="muted" aria-label="No">–</span>}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="muted small" style={{ margin: 0 }}>
        On top of the role, each person can be limited to some devices, to switching hours, and to an end date (People tab). Every switch asks to
        confirm first.
      </p>
    </Section>
  );
}

// ------------------------------------------------------------------ sites and gateways

function SitesAndGateways({ accountId, sites, canEdit, onSitesChanged }: { accountId: string | undefined; sites: Site[]; canEdit: boolean; onSitesChanged?: () => void }) {
  const devices = useLoad(() => api.devices(accountId), [accountId]);
  const [siteEdit, setSiteEdit] = useState<string | null>(null);
  const [siteName, setSiteName] = useState("");
  const [siteError, setSiteError] = useState("");
  const [siteBusy, setSiteBusy] = useState(false);
  const saveSite = async (id: string) => {
    setSiteBusy(true);
    setSiteError("");
    try {
      await api.renameSite(id, siteName.trim(), accountId);
      setSiteEdit(null);
      onSitesChanged?.();
    } catch (e) {
      setSiteError(e instanceof ApiError || e instanceof Error ? e.message : "Couldn't save the name.");
    }
    setSiteBusy(false);
  };
  const gws = useLoad(() => api.gateways(accountId), [accountId]);
  const now = useNow();
  const [renaming, setRenaming] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  if (!devices.data || !gws.data) return <Spinner />;
  const all = devices.data.devices;
  const gateways = [...gws.data.gateways].sort((a, b) => (b.lastHeard ?? 0) - (a.lastHeard ?? 0));
  const save = async (eui: string) => {
    setBusy(true);
    setError("");
    try {
      await api.nameGateway(eui, name.trim(), accountId);
      setRenaming(null);
      gws.reload();
    } catch (e) {
      setError(e instanceof ApiError || e instanceof Error ? e.message : "Couldn't save the name.");
    }
    setBusy(false);
  };
  return (
    <>
      <Section title={`Sites · ${sites.length}`}>
        <div className="card list">
          {sites.map((s) => (
            <div className="row" key={s.id} style={{ flexWrap: "wrap" }}>
              <span className="dot info" />
              <div>
                <div className="t">{s.name}</div>
                <div className="d">
                  {all.filter((d) => d.siteId === s.id).length} devices · {s.timezone}
                </div>
              </div>
              {canEdit && siteEdit !== s.id ? (
                <button
                  className="btn ghost"
                  onClick={() => {
                    setSiteEdit(s.id);
                    setSiteName(s.name);
                    setSiteError("");
                  }}
                >
                  Rename
                </button>
              ) : (
                <span />
              )}
              {siteEdit === s.id && (
                <form
                  className="frow"
                  style={{ flexBasis: "100%" }}
                  onSubmit={(e) => {
                    e.preventDefault();
                    void saveSite(s.id);
                  }}
                >
                  <div className="field">
                    <label htmlFor={`site-${s.id}`}>Farm name</label>
                    <input id={`site-${s.id}`} value={siteName} onChange={(e) => setSiteName(e.target.value)} maxLength={60} autoFocus />
                  </div>
                  <span className="toolbar" style={{ alignSelf: "end" }}>
                    <button className="btn primary" disabled={siteBusy || !siteName.trim()}>
                      Save
                    </button>
                    <button type="button" className="btn" onClick={() => setSiteEdit(null)}>
                      Cancel
                    </button>
                  </span>
                  {siteError && <div className="error small" style={{ flexBasis: "100%" }}>{siteError}</div>}
                </form>
              )}
            </div>
          ))}
        </div>
      </Section>
      <Section title={`Gateways · ${gateways.length}`}>
        <div className="card list">
          {!gateways.length && <div className="empty">No gateway recorded yet. Each device notes the gateway it was last heard through.</div>}
          {gateways.map((g) => {
            // The gateway's own keep-alive says whether it has internet; without one, go by its devices.
            const st = g.online === undefined ? deviceStatus({ lastSeenAt: g.lastHeard }, now) : g.online ? "online" : "offline";
            const list = g.devices.map((eui) => all.find((d) => d.devEui === eui)?.name ?? eui);
            return (
              <div className="row" key={g.eui} style={{ flexWrap: "wrap" }}>
                <span className={`dot ${STATUS_TONE[st]}`} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="t">
                    {g.name ?? `Gateway ${formatEui(g.eui)}`}
                    {g.name && <span className="mono muted small"> {formatEui(g.eui)}</span>}
                  </div>
                  <div className="d">{list.length ? `${list.length} device${list.length === 1 ? "" : "s"}: ${list.join(", ")}` : "No device heard through it yet"}</div>
                </div>
                {g.online === true && <span className="pill good">Online</span>}
                {g.online === false && g.lastKeepaliveAt !== undefined && <span className="pill crit">No internet since {clock(g.lastKeepaliveAt, now)}</span>}
                <span className="small muted">{g.lastHeard ? `device heard ${ago(g.lastHeard, now)}` : ""}</span>
                {canEdit && renaming !== g.eui && (
                  <button
                    className="btn ghost"
                    onClick={() => {
                      setRenaming(g.eui);
                      setName(g.name ?? "");
                    }}
                  >
                    {g.name ? "Rename" : "Name it"}
                  </button>
                )}
                {renaming === g.eui && (
                  <form
                    className="frow"
                    style={{ flexBasis: "100%" }}
                    onSubmit={(e) => {
                      e.preventDefault();
                      void save(g.eui);
                    }}
                  >
                    <div className="field">
                      <label htmlFor={`gw-${g.eui}`}>Name</label>
                      <input id={`gw-${g.eui}`} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Pump shed" maxLength={60} autoFocus />
                    </div>
                    <div className="toolbar" style={{ alignSelf: "end" }}>
                      <button className="btn primary" disabled={busy}>
                        Save
                      </button>
                      <button type="button" className="btn" onClick={() => setRenaming(null)}>
                        Cancel
                      </button>
                    </div>
                    {error && <div className="error">{error}</div>}
                  </form>
                )}
              </div>
            );
          })}
        </div>
        <p className="muted small" style={{ margin: 0 }}>
          Online means the gateway's own once-a-minute keep-alive is arriving, so it has internet; after 10 minutes without one it shows no
          internet and an alert is raised. Gateways that don't send a keep-alive go by when a device was last heard through them. Names are the farm's own (the gateway itself isn't changed) and show on the map
          and device pages.
        </p>
      </Section>
    </>
  );
}

// ------------------------------------------------------------------ settings

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function Settings({ accountId }: { accountId: string | undefined }) {
  const s = useLoad(() => api.settings(accountId), [accountId]);
  if (s.error && !s.data) return <Problem error={s.error} onRetry={s.reload} />;
  if (!s.data) return <Spinner />;
  return <SettingsForm accountId={accountId} initial={s.data.settings} onSaved={s.reload} />;
}

function SettingsForm({ accountId, initial, onSaved }: { accountId: string | undefined; initial: AccountSettings; onSaved: () => void }) {
  const [tariff, setTariff] = useState(initial.tariffCents !== undefined ? String(initial.tariffCents) : "");
  const [licence, setLicence] = useState(initial.licenceML !== undefined ? String(initial.licenceML) : "");
  const [season, setSeason] = useState(String(initial.seasonStartMonth ?? 7));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  return (
    <Section title="Farm settings">
      <form
        className="card pad"
        style={{ display: "flex", flexDirection: "column", gap: 12 }}
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setError("");
          setSaved(false);
          try {
            await api.saveSettings(
              {
                ...(tariff.trim() ? { tariffCents: Number(tariff) } : {}),
                ...(licence.trim() ? { licenceML: Number(licence) } : {}),
                seasonStartMonth: Number(season),
                // Set on the Crops tab; a save here replaces every setting, so carry it.
                ...(initial.askCrop ? { askCrop: true } : {}),
              },
              accountId,
            );
            setSaved(true);
            onSaved();
          } catch (err) {
            setError(message(err));
          } finally {
            setBusy(false);
          }
        }}
      >
        <div className="frow">
          <div className="field">
            <label htmlFor="sTariff">Electricity price (cents per kWh)</label>
            <input id="sTariff" inputMode="decimal" value={tariff} onChange={(e) => setTariff(e.target.value)} placeholder="e.g. 28" />
          </div>
          <div className="field">
            <label htmlFor="sLicence">Water licence allocation (ML per year)</label>
            <input id="sLicence" inputMode="decimal" value={licence} onChange={(e) => setLicence(e.target.value)} placeholder="e.g. 120" />
          </div>
          <div className="field">
            <label htmlFor="sSeason">Season starts</label>
            <select id="sSeason" value={season} onChange={(e) => setSeason(e.target.value)}>
              {MONTHS.map((m, i) => (
                <option key={m} value={i + 1}>
                  1 {m}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="muted small">These feed the water and power cost reports when they're built.</div>
        {error && <div className="error">{error}</div>}
        <div className="toolbar">
          <button className="btn primary" disabled={busy}>
            {busy ? "Saving…" : "Save"}
          </button>
          {saved && <span className="muted small">Saved</span>}
        </div>
      </form>
    </Section>
  );
}

// ------------------------------------------------------------------ crops

const CROP_COLOURS = ["#4caf50", "#8bc34a", "#cddc39", "#ffb300", "#ff7043", "#8d6e63", "#26a69a", "#42a5f5", "#7e57c2", "#ec407a"];

/**
 * The farm's crops. When a pump is switched on (or a running pump is tagged) for a crop, its
 * water, power and run hours are reported under that crop (Reports → Crops).
 */
function Crops({ accountId, canEdit }: { accountId: string | undefined; canEdit: boolean }) {
  const allowed = useCan("crops");
  if (!allowed) {
    return (
      <Section title="Crops">
        <LockCard feature="crops" />
      </Section>
    );
  }
  return <CropsBody accountId={accountId} canEdit={canEdit} />;
}

function CropsBody({ accountId, canEdit }: { accountId: string | undefined; canEdit: boolean }) {
  const list = useLoad(() => api.crops(accountId), [accountId]);
  const [name, setName] = useState("");
  const [area, setArea] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  if (list.error && !list.data) return <Problem error={list.error} onRetry={list.reload} />;
  if (!list.data) return <Spinner />;
  const crops = list.data.crops.filter((c) => showArchived || !c.archived);
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError("");
    try {
      await fn();
      list.reload();
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Section title="Crops">
      <p className="muted small" style={{ margin: 0 }}>
        When someone switches a pump on for a crop, or tags a pump that's already running, its water, power and run hours are reported under that crop
        (Reports → Crops). Archive a crop when it's finished: its history stays.
      </p>
      {canEdit && (
        <label className="checkline">
          <input
            type="checkbox"
            checked={list.data.askCrop}
            disabled={busy}
            onChange={(e) =>
              void run(async () => {
                const { settings } = await api.settings(accountId);
                const { askCrop: _old, ...rest } = settings;
                await api.saveSettings({ ...rest, ...(e.target.checked ? { askCrop: true } : {}) }, accountId);
              })
            }
          />
          <span>Ask which crop every time a pump is switched on</span>
        </label>
      )}
      <div className="card list">
        {!crops.length && <div className="empty">No crops yet.</div>}
        {crops.map((c) => (
          <CropRow key={c.id} crop={c} accountId={accountId} canEdit={canEdit} onSaved={list.reload} />
        ))}
      </div>
      {list.data.crops.some((c) => c.archived) && (
        <button className="btn ghost" style={{ alignSelf: "flex-start" }} onClick={() => setShowArchived(!showArchived)}>
          {showArchived ? "Hide archived crops" : "Show archived crops"}
        </button>
      )}
      {canEdit && (
        <form
          className="card pad frow"
          onSubmit={(e) => {
            e.preventDefault();
            void run(async () => {
              await api.addCrop({ name: name.trim(), ...(area.trim() ? { areaHa: Number(area) } : {}), colour: CROP_COLOURS[list.data!.crops.length % CROP_COLOURS.length]! }, accountId);
              setName("");
              setArea("");
            });
          }}
        >
          <div className="field">
            <label htmlFor="cropName">New crop</label>
            <input id="cropName" value={name} onChange={(e) => setName(e.target.value.slice(0, 60))} placeholder="e.g. Lucerne – Pivot A" />
          </div>
          <div className="field">
            <label htmlFor="cropArea">Area (ha, optional)</label>
            <input id="cropArea" inputMode="decimal" value={area} onChange={(e) => setArea(e.target.value)} placeholder="e.g. 42" />
          </div>
          <button className="btn primary" disabled={busy || !name.trim() || (area.trim() !== "" && !(Number(area) > 0))}>
            Add crop
          </button>
        </form>
      )}
      {error && <div className="error">{error}</div>}
    </Section>
  );
}

function CropRow({ crop, accountId, canEdit, onSaved }: { crop: Crop; accountId: string | undefined; canEdit: boolean; onSaved: () => void }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(crop.name);
  const [area, setArea] = useState(crop.areaHa !== undefined ? String(crop.areaHa) : "");
  const [colour, setColour] = useState(crop.colour ?? CROP_COLOURS[0]!);
  const [error, setError] = useState("");
  const save = async (body: Parameters<typeof api.updateCrop>[1]) => {
    setError("");
    try {
      await api.updateCrop(crop.id, body, accountId);
      setEditing(false);
      onSaved();
    } catch (e) {
      setError(message(e));
    }
  };
  return (
    <div className="ctl" style={{ flexWrap: "wrap" }}>
      <div style={{ display: "flex", gap: 10, alignItems: "center", minWidth: 0 }}>
        <span className="crop-swatch" style={{ background: crop.colour ?? "var(--muted)" }} />
        <div>
          <div className="t">{crop.name}</div>
          <div className="d">
            {crop.areaHa !== undefined ? `${crop.areaHa} ha` : "Area not set"}
            {crop.archived ? " · archived" : ""}
          </div>
        </div>
      </div>
      {canEdit && !editing && (
        <div className="ctl-actions">
          <button className="btn" onClick={() => setEditing(true)}>
            Edit
          </button>
          <button className="btn ghost" onClick={() => void save({ archived: !crop.archived })}>
            {crop.archived ? "Bring back" : "Archive"}
          </button>
        </div>
      )}
      {editing && (
        <div className="ctl-note frow" style={{ flexBasis: "100%" }}>
          <div className="field">
            <label htmlFor={`cn-${crop.id}`}>Name</label>
            <input id={`cn-${crop.id}`} value={name} onChange={(e) => setName(e.target.value.slice(0, 60))} />
          </div>
          <div className="field">
            <label htmlFor={`ca-${crop.id}`}>Area (ha)</label>
            <input id={`ca-${crop.id}`} inputMode="decimal" value={area} onChange={(e) => setArea(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor={`cc-${crop.id}`}>Colour</label>
            <input id={`cc-${crop.id}`} type="color" value={colour} onChange={(e) => setColour(e.target.value)} />
          </div>
          <div className="toolbar">
            <button
              className="btn primary"
              disabled={!name.trim() || (area.trim() !== "" && !(Number(area) > 0))}
              onClick={() => void save({ name: name.trim(), areaHa: area.trim() ? Number(area) : null, colour })}
            >
              Save
            </button>
            <button className="btn ghost" onClick={() => setEditing(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
      {error && <div className="error ctl-note">{error}</div>}
    </div>
  );
}

// ------------------------------------------------------------------ plan

const money = (d: number) => d.toLocaleString("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 });

/**
 * The plan the farm is on, what it includes and how much is used, and the other plans. There's
 * no card payment yet: "Upgrade" sends FMS a request, and they switch the plan over.
 */
function PlanTab({ accountId, staff, role }: { accountId: string | undefined; staff: boolean; role: string }) {
  const data = useLoad(() => api.plan(accountId), [accountId]);
  const [asking, setAsking] = useState<Plan | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  if (data.error && !data.data) return <Problem error={data.error} onRetry={data.reload} />;
  if (!data.data) return <Spinner />;
  const { entitlement: e, plans, used, free, pending, payments, payOnline, legacy } = data.data;
  const cur = plans.find((p) => p.id === e.effective)!;
  const own = plans.find((p) => p.id === e.plan)!;
  const rank = (p: Plan) => plans.findIndex((x) => x.id === p);
  const pct = (n: number, limit: number | null) => (limit === null ? 0 : Math.min(100, (100 * n) / limit));
  const rows: Array<[string, (p: PlanInfo) => string, (p: PlanInfo) => boolean]> = [
    ["Devices included", (p) => (p.devices === null ? "Unlimited" : String(p.devices)), () => true],
    ["Extra device", (p) => (p.extraDevice === null ? "Upgrade needed" : p.extraDevice === 0 ? "Included" : `${money(p.extraDevice)}/month`), (p) => p.extraDevice !== null],
    ["People", (p) => (p.users === null ? "Unlimited" : String(p.users)), () => true],
    ["History kept", (p) => (p.historyDays === null ? "Everything" : p.historyDays >= 365 ? `${Math.round(p.historyDays / 365)} year${p.historyDays >= 730 ? "s" : ""}` : `${p.historyDays} days`), () => true],
    ["Switch pumps", (p) => (p.features.includes("control") ? "Yes" : "View only"), (p) => p.features.includes("control")],
    ["Programs", (p) => (!p.features.includes("programs") ? "–" : p.programs ? `${p.programs.rules} rules + ${p.programs.schedules} schedule` : "Unlimited"), (p) => p.features.includes("programs")],
    ["Insights", (p) => (p.features.includes("insights") ? "Yes" : "–"), (p) => p.features.includes("insights")],
    ["Ask AI", (p) => (p.features.includes("ask-ai") ? "Yes" : "–"), (p) => p.features.includes("ask-ai")],
    ["Crops and reports", (p) => (p.features.includes("crops") ? "Yes" : "–"), (p) => p.features.includes("crops")],
    ["SMS alerts a month", (p) => (p.smsPerMonth ? String(p.smsPerMonth) : "–"), (p) => p.smsPerMonth > 0],
    ["API and integrations", (p) => (p.features.includes("api") ? "Yes" : "–"), (p) => p.features.includes("api")],
  ];
  const request = async (plan: Plan) => {
    setBusy(true);
    setError("");
    try {
      await api.requestPlan(plan, note, accountId);
      setAsking(null);
      setNote("");
      data.reload();
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Section title="Plan">
      {e.trialDaysLeft && (
        <div className="card trial">
          <div>
            <span className="pro-badge">FARM PRO TRIAL</span>
            <h3 style={{ fontSize: 19, marginTop: 6 }}>
              {e.trialDaysLeft} day{e.trialDaysLeft === 1 ? "" : "s"} left on your free Farm Pro trial
            </h3>
            <p className="muted small" style={{ margin: "4px 0 0" }}>
              After that the farm goes back to the {own.name} plan. Keep Ask AI, five years of history and the rest by moving to Farm Pro.
            </p>
          </div>
          {e.plan !== "farm-pro" && !pending && (
            <button className="btn primary" onClick={() => setAsking("farm-pro")}>
              Keep Farm Pro
            </button>
          )}
        </div>
      )}
      {free && <div className="banner info small">FMS isn't charging this farm at the moment.</div>}
      <OwnerPayment legacy={legacy} payments={payments} payOnline={payOnline} owner={role === "owner" || staff} free={free} accountId={staff ? accountId : undefined} />
      {pending && (
        <div className="banner info small">
          FMS has your request to move to <b>{plans.find((p) => p.id === pending.plan)?.name}</b> ({new Date(pending.at).toLocaleDateString("en-AU")}). They'll be in touch to switch it over.
        </div>
      )}
      <div className="tiles">
        <div className="tile">
          <div className="lbl">Your plan</div>
          <div className="big">{own.name}</div>
          <div className="foot">
            {legacy && legacy.until > Date.now()
              ? `$${legacy.price} a month (FMS-Control price) until ${new Date(legacy.until).toLocaleDateString("en-AU")}, then ${own.price === null ? "by quote" : money(own.price)}`
              : own.price === null
                ? "By quote"
                : `${money(own.price)} a month inc. GST`}
          </div>
        </div>
        <div className="tile">
          <div className="lbl">Devices</div>
          <div className="big num">
            {used.devices}
            {cur.devices !== null && <small>of {cur.devices}</small>}
          </div>
          <div className="progress" style={{ marginTop: 6 }}>
            <div style={{ width: `${pct(used.devices, cur.devices)}%`, background: cur.devices !== null && used.devices > cur.devices ? "var(--warn)" : undefined }} />
          </div>
          {cur.devices !== null && used.devices > cur.devices && (
            <div className="foot" style={{ color: "var(--warn)" }}>
              {used.devices - cur.devices} over: {cur.extraDevice === null ? "upgrade needed" : `${money(cur.extraDevice)}/month each`}
            </div>
          )}
        </div>
        <div className="tile">
          <div className="lbl">People</div>
          <div className="big num">
            {used.users}
            {cur.users !== null && <small>of {cur.users}</small>}
          </div>
          <div className="progress" style={{ marginTop: 6 }}>
            <div style={{ width: `${pct(used.users, cur.users)}%` }} />
          </div>
        </div>
        <div className="tile">
          <div className="lbl">History kept</div>
          <div className="big">{cur.historyDays === null ? "All" : cur.historyDays >= 365 ? `${Math.round(cur.historyDays / 365)} yr` : `${cur.historyDays} d`}</div>
          <div className="foot">Older readings are kept and come back when you upgrade.</div>
        </div>
      </div>

      {asking && (
        <div className="card pad" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <b>
            {asking === "enterprise" ? "Ask FMS about Enterprise" : `Move to ${plans.find((p) => p.id === asking)?.name}`}
          </b>
          <p className="muted small" style={{ margin: 0 }}>
            FMS will set the plan up and confirm the price with you. No payment is taken online.
          </p>
          <div className="field">
            <label htmlFor="planNote">Anything to add (optional)</label>
            <input id="planNote" value={note} onChange={(ev) => setNote(ev.target.value.slice(0, 500))} placeholder="e.g. we're adding 6 more pumps in March" />
          </div>
          <div className="toolbar">
            <button className="btn primary" disabled={busy} onClick={() => void request(asking)}>
              {busy ? "Sending…" : "Send request"}
            </button>
            <button className="btn ghost" onClick={() => setAsking(null)}>
              Cancel
            </button>
          </div>
          {error && <div className="error">{error}</div>}
        </div>
      )}

      <div className="plans">
        {plans.map((p) => {
          const mine = p.id === e.plan;
          const up = rank(p.id) > rank(e.plan);
          return (
            <div key={p.id} className={`card plan ${p.id === "farm-pro" ? "best" : ""}`}>
              {p.id === "farm-pro" ? <span className="pro-badge" style={{ alignSelf: "flex-start" }}>MOST POPULAR</span> : <span style={{ height: 19 }} />}
              <div className="lbl">
                {p.name}
                {mine ? " · your plan" : ""}
              </div>
              <div className="big num">
                {p.price === null ? "Quote" : money(p.price)}
                {p.price !== null && <small>/month</small>}
              </div>
              <div className="muted small">{p.blurb}</div>
              <ul className="feat">
                {rows.map(([label, text, on]) => (
                  <li key={label} className={on(p) ? "" : "off"}>
                    <span>{label}</span>
                    <b>{text(p)}</b>
                  </li>
                ))}
              </ul>
              {mine ? (
                <button className="btn" disabled>
                  Current plan
                </button>
              ) : staff ? (
                <button className="btn" disabled>
                  Set on the customer's page
                </button>
              ) : (
                <button className={`btn ${up ? "primary" : ""}`} disabled={!!pending || busy} onClick={() => setAsking(p.id)}>
                  {p.id === "enterprise" ? "Talk to FMS" : up ? "Upgrade" : "Switch"}
                </button>
              )}
            </div>
          );
        })}
      </div>
      <p className="muted small" style={{ margin: 0 }}>
        Prices inc. GST. Changing plan is arranged with FMS.
      </p>
    </Section>
  );
}

// ------------------------------------------------------------------ activity log

function Log({ accountId }: { accountId: string | undefined }) {
  const log = useLoad(() => api.accountAudit(accountId), [accountId], 60_000);
  const now = useNow();
  const [q, setQ] = useState("");
  if (log.error && !log.data) return <Problem error={log.error} onRetry={log.reload} />;
  if (!log.data) return <Spinner />;
  const term = q.trim().toLowerCase();
  const list = log.data.entries.filter((e) => !term || `${e.actor} ${e.action} ${e.detail} ${AUDIT_NAMES[e.action] ?? ""}`.toLowerCase().includes(term));
  return (
    <Section title="Activity log">
      <input className="search" type="search" placeholder="Search who or what" value={q} onChange={(e) => setQ(e.target.value)} />
      <div className="card list">
        {!list.length && <div className="empty">Nothing found.</div>}
        {list.map((e, i) => (
          <div className="row" key={i}>
            <span className="dot info" />
            <div>
              <div className="t">{AUDIT_NAMES[e.action] ?? e.action}</div>
              <div className="d">
                {e.detail ? `${e.detail} · ` : ""}
                {e.actor}
              </div>
            </div>
            <time>{clock(e.at, now)}</time>
          </div>
        ))}
      </div>
      <p className="muted small" style={{ margin: 0 }}>
        Every switch, change and sign-in is recorded.
      </p>
    </Section>
  );
}
